import React from 'react';

import { Container, ProgressBar, ProgressBarContainer } from './styles';
import MedflowLogo from '../Logo/MedflowLogo';

interface ContentObject {
  title: string;
  description: string;
}

type RenderContent = () => React.ReactElement | null;

interface Props {
  promise: Promise<unknown>;
  content: ContentObject | RenderContent;
  progressStep?: number;
  intervalMs?: number;
  animationMs?: number;
  endingTimeoutMs?: number;
  onDone?: () => void;
}

const EndingTransitionLoading: React.FC<Props> = ({
  promise,
  intervalMs = 300,
  animationMs = 300,
  endingTimeoutMs = 600,
  progressStep,
  content,
  onDone,
}) => {
  const [progress, setProgress] = React.useState(0);

  const Content = React.useMemo(() => {
    if (typeof content === 'function') return content();

    const { title, description } = content;
    return (
      <>
        <h3>{title}</h3>

        <p>{description}</p>
      </>
    );
  }, [content]);

  React.useEffect(() => {
    let isMounted = true;
    let interval: NodeJS.Timeout;

    const simulateProgress = () => {
      interval = setInterval(
        () =>
          setProgress(prev => {
            if (prev < 90) return prev + (progressStep || Math.random() * 10);
            return prev;
          }),
        intervalMs,
      );
    };

    simulateProgress();

    promise.then(() => {
      if (!isMounted) return;
      setProgress(100);
      setTimeout(() => {
        clearInterval(interval);
        if (onDone) onDone();
      }, endingTimeoutMs);
    });

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [endingTimeoutMs, intervalMs, onDone, progressStep, promise]);

  return (
    <Container>
      <MedflowLogo />

      {Content}

      <ProgressBarContainer>
        <ProgressBar progress={progress} transitionMs={animationMs} />
      </ProgressBarContainer>
    </Container>
  );
};

export default EndingTransitionLoading;
